/* eslint-disable no-use-before-define */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CameraScanChild from "./CameraScanChild";
import posthog from "../../utils/posthog";
import Webcam from "react-webcam";
import { ScanningComponentProps } from "../../types/interfaces";
import useTensorFlow from "../../customHooks/useTensorFlow";
import ParamsContext from "../../utils/context/paramsContext";
import {
	generateUuid,
	getCurrentTimeInSeconds,
	getPreferredMediaRecorderTypes,
	getRecordingExtension,
	getRecordingMimeType,
	getSupportedMediaRecorderTypes,
	handleScanTimeCapture,
	rescanSupportCaptureEvent,
} from "../../utils/utils";
import speechService from "../../utils/service/speechService";
import { videoConstraintsExact, videoConstraintsFallback, videoTypes, voiceOverAssetsPath } from "../../utils/constants";

let audioTimeoutId: ReturnType<typeof setTimeout> | undefined;
let lastVideoPlayed: {
	skipCount: number;
	no_of_times_skipped: number;
	audioName: string;
} | null = null;

function ScanningComponent({ setIsScanLocked, resetDetector, scanID, setIsVideoUploaded, setScanFailsError, setScanStartTime, setScanUniqueKey, userDetails }: Omit<ScanningComponentProps, "config">) {
	const { gender, heightInCm, email, shopDomain } = userDetails;
	const webcamRef = useRef<Webcam | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const recordedFpsRef = useRef<number | null>(null);
	const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
	const [loadingCam, setLoadingCam] = useState(true);
	const [recordingStarted, setRecordingStarted] = useState(false);
	const [faceDone, setFaceDone] = useState(false);
	const [mediaRecorderStopped, setMediaRecorderStopped] = useState(true);
	const [isScanning, setIsScanning] = useState(false);
	const captureVideoTimeOutHandle = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [audioSource, setAudioSource] = useState("");
	const [audioSourceList, setAudioSourceList] = useState<string[]>([]);
	const [emptyAudioSource, setEmptyAudioSource] = useState("");
	const [startAgain, setStartAgain] = useState(false);
	const [showRestart, setRestart] = useState(false);
	const [pause, setPause] = useState(false);
	const [events, setEvents] = useState<any>([]);
	const [showPause, setShowPause] = useState(false);
	const allowAudioToPlay = useRef(true);
	const { poseDetector } = useTensorFlow();
	const firstScan = useRef(true);
	const poseStoppedRef = useRef(false);
	const counterRef = useRef(0);
	const consecutiveFalseRef = useRef(0);
	const poseListenerRegistered = useRef(false);
	const isConnectingRef = useRef(false);
	const brilliantPlayedRef = useRef(false);
	const postPoseProcessRef = useRef<() => void>(() => {});
	const startSendingVideoFramesRef = useRef<() => void>(() => {});
	const idRef = useRef<string | null>(null);
	const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
	const [webcamKey, setWebcamKey] = useState(0);
	const [currentVideoConstraints, setCurrentVideoConstraints] = useState<MediaTrackConstraints>(videoConstraintsExact);

	const { setStartGyro, uploadScanFile, setUploadLoading, swan, onScanStart, onCaptureComplete } = useContext(ParamsContext);

	const resetScan = () => {
		poseStoppedRef.current = false;
		clearTimeout(audioTimeoutId);
		if (captureVideoTimeOutHandle.current) clearTimeout(captureVideoTimeOutHandle.current);
		setScanUniqueKey(generateUuid());
		setScanFailsError("");
		setUploadLoading?.(false);
		setRecordedChunks([]);
		recordedFpsRef.current = null;
		setLoadingCam(true);
		setRecordingStarted(false);
		setFaceDone(false);
		setMediaRecorderStopped(true);
		setIsScanning(false);
		captureVideoTimeOutHandle.current = null;
		setAudioSource("");
		setAudioSourceList([]);
		setEmptyAudioSource("");
		setStartAgain(!startAgain);
		setStartGyro(false);
		speechService.stopAudio();
		setRestart(false);
		setPause(false);
		counterRef.current = 0;
		consecutiveFalseRef.current = 0;
		poseListenerRegistered.current = false;
		brilliantPlayedRef.current = false;
		if (mediaRecorderRef.current !== null) {
			mediaRecorderRef.current.stop();
		}

		events.forEach((el: any) => {
			if (mediaRecorderRef.current) mediaRecorderRef.current.removeEventListener("dataavailable", el);
		});

		setEvents([]);
		allowAudioToPlay.current = true;
		setShowPause(false);
		setIsVideoUploaded(false);
		if (webcamRef.current?.stream) {
			setLoadingCam(false);
		}
	};

	const handleUserMedia = useCallback(() => {
		setTimeout(() => {
			setLoadingCam(false);
		}, 1000);
	}, []);

	const handleUserMediaError = useCallback(
		(error: string | DOMException) => {
			console.log("camera error", error);
			// Retry once with relaxed constraints for broader device compatibility.
			if (currentVideoConstraints === videoConstraintsExact) {
				setCurrentVideoConstraints(videoConstraintsFallback);
				setWebcamKey((prev) => prev + 1);
				return;
			}
			setLoadingCam(false);
		},
		[currentVideoConstraints],
	);

	const handlePause = useCallback(() => {
		allowAudioToPlay.current = false;
		setPause(true);
		setShowPause(false);
		if (captureVideoTimeOutHandle.current) clearTimeout(captureVideoTimeOutHandle.current);
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current.pause();
		}
		speechService.stopAudio();
		swan.poseDetection.disconnect();
		posthog.capture(`${shopDomain}/pose_detection_disconnected`, {
			scanID: scanID,
			email,
			id: idRef.current,
			reason: "pause",
		});
		counterRef.current = 0;
		consecutiveFalseRef.current = 0;
		poseListenerRegistered.current = false;
		brilliantPlayedRef.current = false;
		if (mediaRecorderRef.current) {
			mediaRecorderRef.current.stop();
		}
		events.forEach((el: any) => {
			if (mediaRecorderRef.current) mediaRecorderRef.current.removeEventListener("dataavailable", el);
		});
		setEvents([]);
		clearTimeout(audioTimeoutId);
	}, [mediaRecorderRef, events, speechService, allowAudioToPlay]);

	// const supportedTypes = videoTypes.filter((type) => MediaRecorder.isTypeSupported(type));
	const preferredVideoTypes = useMemo(() => getPreferredMediaRecorderTypes(), []);
	const recordingMimeType = getRecordingMimeType(supportedTypes);
	const recordingExtension = getRecordingExtension(recordingMimeType);

	const stopRecording = useCallback(async () => {
		allowAudioToPlay.current = true;
		await speechService.playAudio(`${voiceOverAssetsPath}SpotOn.mp3`);
		setPause(false);
		setShowPause(false);
		if (captureVideoTimeOutHandle.current) clearTimeout(captureVideoTimeOutHandle.current);
		setRecordingStarted(false);
	}, [captureVideoTimeOutHandle, speechService]);

	const handleSocket = useCallback(async () => {
		// Guard against React StrictMode double-mount: skip if already connecting or connected
		if (isConnectingRef.current || idRef.current) return;
		isConnectingRef.current = true;
		try {
			idRef.current = await swan.poseDetection.connect();

			posthog.capture(`${shopDomain}/pose_detection_connected`, {
				scanID: scanID,
				email,
				id: idRef.current,
			});
		} catch (error) {
			console.log(error, "while connecting websocket");
			posthog.capture(`${shopDomain}/pose_detection_connect_failed`, {
				scanID: scanID,
				email,
				error: String(error),
			});
		} finally {
			isConnectingRef.current = false;
		}
	}, [scanID, shopDomain, email]);

	const handleReScan = useCallback(async () => {
		rescanSupportCaptureEvent({
			eventName: `${shopDomain}/rescan`,
			email,
			scanID: scanID,
			height: heightInCm,
			gender,
			status: false,
		});
		allowAudioToPlay.current = false;
		swan.poseDetection.disconnect();
		posthog.capture(`${shopDomain}/pose_detection_disconnected`, {
			scanID: scanID,
			email,
			id: idRef.current,
			reason: "rescan",
		});
		idRef.current = null; // reset so handleSocket guard allows reconnection
		resetScan();
		await handleSocket();
		startSendingVideoFramesRef.current();
	}, [handleSocket, scanID, email]);

	const handleSpinDataAvailable = useCallback(
		({ data }: BlobEvent) => {
			if (data && data.size > 0 && allowAudioToPlay.current) {
				setRecordedChunks((prev) => prev.concat(data));
				if (!poseStoppedRef.current && webcamRef.current) {
					poseStoppedRef.current = true;
					poseDetector(() => {
						stopRecording();
						handleScanTimeCapture({
							eventName: `${shopDomain}/tensorFlow`,
							scanID: scanID,
							email,
							message: "recording stopped by tensorflow ",
						});
					}, webcamRef);
				}
			}
		},
		[stopRecording, handleScanTimeCapture, shopDomain, scanID, email, webcamRef],
	);

	const playAudio = useCallback(async () => {
		const lastAudio = audioSourceList[audioSourceList.length - 1];
		if (lastAudio && allowAudioToPlay.current) {
			await speechService.playAudio(voiceOverAssetsPath + lastAudio);
			if (allowAudioToPlay.current) {
				audioTimeoutId = setTimeout(playAudio, 2000);
			}
		}
	}, [audioSourceList, allowAudioToPlay]);

		const handleStartCaptureClick = useCallback(() => {
		if (mediaRecorderRef && mediaRecorderRef.current) {
			mediaRecorderRef.current.stop();
		}
		setRecordingStarted(true);
		try {
			if (webcamRef && webcamRef.current && webcamRef.current.stream) {
				// Capture FPS from stream settings
				const videoTrack = webcamRef.current.stream.getVideoTracks()[0];
				if (videoTrack) {
					const settings = videoTrack.getSettings();
					recordedFpsRef.current = settings.frameRate || null;
				}

				const mediaRecorderOptions: MediaRecorderOptions = {
					videoBitsPerSecond: 1_500_000,
				};
				if (supportedTypes[0]) {
					mediaRecorderOptions.mimeType = supportedTypes[0];
				}
				mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, mediaRecorderOptions);
				mediaRecorderRef.current.addEventListener("dataavailable", handleSpinDataAvailable);

				// Mark recorder as fully stopped only after all dataavailable events have fired
				mediaRecorderRef.current.addEventListener("stop", () => {
					setMediaRecorderStopped(true);
				});

				setEvents([...events, handleSpinDataAvailable]);
				mediaRecorderRef.current.start(1000);
				setMediaRecorderStopped(false);
			}
		} catch (e) {
			console.log("error while using media recorder", e);
		}
	}, [webcamRef, supportedTypes, handleSpinDataAvailable, events]);

	const postPoseProcess = useCallback(async () => {
		if (captureVideoTimeOutHandle.current) {
			clearTimeout(captureVideoTimeOutHandle.current);
		}
		handleStartCaptureClick();
		if (allowAudioToPlay.current) {
			captureVideoTimeOutHandle.current = setTimeout(async () => {
				if (allowAudioToPlay.current) {
					await speechService.playAudio(`${voiceOverAssetsPath}SpotOn.mp3`);
					setRecordingStarted(false);
				}
			}, 15000);
		}
		setFaceDone(true);
		if (allowAudioToPlay.current) {
			await speechService.playAudio(`${voiceOverAssetsPath}Spin.mp3`);
		}
	}, [handleStartCaptureClick, allowAudioToPlay, speechService]);

	useEffect(() => {
		postPoseProcessRef.current = postPoseProcess;
	}, [postPoseProcess]);

	const handleDataAvailable = useCallback(
		({ data }: BlobEvent) => {
			const connected = swan.poseDetection.connected();
			if (data.size > 0 && connected) {
				if (!poseListenerRegistered.current) {
					poseListenerRegistered.current = true;
					swan.poseDetection.poseStatus(async (data: any) => {
						if (data && data.audio && data.audio.length > 0) {
							const audio = document.querySelector("#audioElement") as HTMLAudioElement | null;

							posthog.capture(`${shopDomain}/pose_instruction_received`, {
								scanID: scanID,
								email,
								audio: data.audio,
								status: data.status,
								sid: data.sid,
								counter: counterRef.current,
								consecutiveFalse: consecutiveFalseRef.current,
								type:"body_scan_instruction",
							});
							if (data.status === true && data.sid === idRef.current) {
								consecutiveFalseRef.current = 0;
								if (counterRef.current < 2) {
									counterRef.current += 1;
									if (counterRef.current === 1 && !brilliantPlayedRef.current) {
										brilliantPlayedRef.current = true;
										speechService.stopAudio();
										await speechService.playAudio(voiceOverAssetsPath + data.audio);
									} else if (counterRef.current === 2 && audio?.paused) {
										speechService.playAudio(voiceOverAssetsPath + data.audio);
									}
								} else {
									setEmptyAudioSource(data.audio);
									clearTimeout(audioTimeoutId);
									swan.poseDetection.disconnect();
									posthog.capture(`${shopDomain}/pose_detection_disconnected`, {
										scanID: scanID,
										email,
										id: idRef.current,
										reason: "pose_confirmed",
									});
									poseListenerRegistered.current = false;
									setTimeout(() => postPoseProcessRef.current(), 1000);
								}
							} else {
								consecutiveFalseRef.current += 1;
								if (consecutiveFalseRef.current >= 3) {
									counterRef.current = 0;
									brilliantPlayedRef.current = false;
								}
								if (audio?.paused && (!lastVideoPlayed || lastVideoPlayed?.audioName !== data.audio)) {
									lastVideoPlayed = {
										skipCount: 2,
										no_of_times_skipped: 0,
										audioName: data.audio,
									};
									speechService.playAudio(voiceOverAssetsPath + data.audio);
								} else if (lastVideoPlayed?.audioName === data.audio && audio?.paused) {
									if (lastVideoPlayed && lastVideoPlayed.no_of_times_skipped >= lastVideoPlayed.skipCount) {
										lastVideoPlayed.no_of_times_skipped = 0;
										speechService.playAudio(voiceOverAssetsPath + data.audio);
									} else {
										if (lastVideoPlayed) lastVideoPlayed.no_of_times_skipped += 1;
									}
								}
							}
						}
					});
				}
				if (webcamRef?.current && webcamRef.current.getScreenshot() !== null) {
					swan.poseDetection.videoEmit({
						image: webcamRef.current.getScreenshot() || "",
						scanId: scanID,
					});
				}
			}
		},
		[webcamRef, scanID, swan, speechService, shopDomain, email],
	);

	const startSendingVideoFrames = useCallback(async () => {
		setIsScanning(true);
		setStartGyro(true);
		onScanStart?.();
		if (firstScan.current) {
			firstScan.current = false;
			handleScanTimeCapture({
				eventName: "scan started",
				scanID: scanID,
				status: "success",
				email,
			});
		}
		setScanStartTime(getCurrentTimeInSeconds());
		setLoadingCam(true);
		if (allowAudioToPlay.current) {
			await speechService.playAudio(`${voiceOverAssetsPath}StartScan.mp3`);
		}
		if (allowAudioToPlay.current) {
			await speechService.playAudio(`${voiceOverAssetsPath}LiftArmsAndHoldAtHip.mp3`);
		}
		if (allowAudioToPlay.current) {
			setIsScanning(false);
			setRecordingStarted(true);
			setRestart(true);
			setShowPause(true);
			handleUserMedia();
		}
		try {
			if (webcamRef && webcamRef.current && webcamRef.current.stream && allowAudioToPlay.current) {
				// Capture FPS from stream settings
				const videoTrack = webcamRef.current.stream.getVideoTracks()[0];
				if (videoTrack) {
					const settings = videoTrack.getSettings();
					recordedFpsRef.current = settings.frameRate || null;
				}
				
				const mediaRecorderOptions: MediaRecorderOptions = {
					videoBitsPerSecond: 1_500_000,
				};
				if (supportedTypes[0]) {
					mediaRecorderOptions.mimeType = supportedTypes[0];
				}
				if (allowAudioToPlay.current) {
					mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, mediaRecorderOptions);
				}
				if (allowAudioToPlay.current && mediaRecorderRef.current) {
					mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
				}

				setEvents([...events, handleDataAvailable]);
				if (mediaRecorderRef.current) mediaRecorderRef.current.start(1000);
				setMediaRecorderStopped(false);
				if (allowAudioToPlay.current) {
					audioTimeoutId = setTimeout(playAudio, 2000);
				}
			}
		} catch (err) {
			console.log("error ----------", err);
		}
	}, [webcamRef, supportedTypes, handleDataAvailable, events, playAudio, allowAudioToPlay, onScanStart]);

	useEffect(() => {
		if (shopDomain) {
			setIsScanLocked(true);
			handleSocket();
		}
		return () => {
			// Capture then reset id so next genuine mount can reconnect.
			// Do NOT reset isConnectingRef here — keeping it true blocks the
			// StrictMode second-mount call while the first connect is still awaiting.
			const prevId = idRef.current;
			idRef.current = null;
			if (prevId) {
				swan.poseDetection.disconnect();
				posthog.capture(`${shopDomain}/pose_detection_disconnected`, {
					scanID: scanID,
					email,
					id: prevId,
					reason: "unmount",
				});
			}
			events.forEach((el: any) => {
				mediaRecorderRef?.current?.removeEventListener("dataavailable", el);
			});
		};
	}, [shopDomain]);
	useEffect(() => {
		audioSourceList.push(audioSource);
	}, [audioSource]);

	useEffect(() => {
		setAudioSourceList([]);
	}, [emptyAudioSource]);
	useEffect(() => {
		const bypassChecksToBackup = recordedChunks.length && recordedChunks.length > 0;
		if (!mediaRecorderStopped && bypassChecksToBackup && !recordingStarted && allowAudioToPlay.current && !pause) {
			if (mediaRecorderRef && mediaRecorderRef.current) {
				if (!recordingStarted) {
					mediaRecorderRef.current.stop();
					// mediaRecorderStopped will be set by the "stop" event listener
					// which fires AFTER all dataavailable events are done
				}
			}
		}
	}, [mediaRecorderStopped, recordingStarted, recordedChunks, pause]);

	useEffect(() => {
		const bypassChecksToBackup = recordedChunks.length && recordedChunks.length > 0;
		if (mediaRecorderStopped && bypassChecksToBackup && !recordingStarted) {
			if (mediaRecorderRef && mediaRecorderRef.current && allowAudioToPlay.current && !pause) {
				if (!recordingStarted) {
					const videoFile = new File(recordedChunks, `${scanID}.${recordingExtension}`, {
						type: recordingMimeType,
					});
					onCaptureComplete?.();
					setUploadLoading?.(true);
					setScanFailsError("");
					// Pass FPS captured at recording time
					uploadScanFile?.(videoFile, recordedFpsRef.current);
				}
			}
		}
	}, [mediaRecorderStopped, recordingStarted, recordedChunks, pause, mediaRecorderRef, allowAudioToPlay]);
	useEffect(() => {
		startSendingVideoFramesRef.current = startSendingVideoFrames;
	}, [startSendingVideoFrames]);

	useEffect(() => {
		const supported = getSupportedMediaRecorderTypes(preferredVideoTypes, videoTypes);
		setSupportedTypes(supported);
	}, [preferredVideoTypes]);
	useEffect(() => {
		resetScan();
	}, []);

	return (
		<CameraScanChild
			resetDetector={resetDetector}
			resetScan={resetScan}
			loadingCam={loadingCam}
			onPause={handlePause}
			showRestart={showRestart}
			pause={pause}
			onReScan={handleReScan}
			recordingStarted={recordingStarted}
			isScanning={isScanning}
			startSendingVideoFrames={startSendingVideoFrames}
			faceDone={faceDone}
			stopRecording={stopRecording}
			showPause={showPause}
			webcamRef={webcamRef}
			onUserMedia={handleUserMedia}
			onUserMediaError={handleUserMediaError}
			videoConstraints={currentVideoConstraints}
			webcamKey={webcamKey}
			/>
	);
}

export default React.memo(ScanningComponent);
